<template>
    <b-modal title-tag="h2" title="New Letter" :lazy="true" :visible="isOpen">
        <form>
            <b-form-group label-cols="3" label="Template:" label-for="template">
                <vue-select
                    v-model="template"
                    label="name"
                    :placeholder="templatePlaceholder"
                    :disabled="isLoadingList"
                    :options="templateOptions"
                    class="show-actions"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Title:" label-for="title">
                <b-form-input v-model="title" placeholder="Title (Optional)" />
            </b-form-group>

            <b-form-group label-cols="3" label="Date:" label-for="date">
                <date-picker
                    :config="datePickerOptions"
                    v-model="letterDate"
                    name="letterDate"
                    placeholder="Date (required)"
                    :class="{
                        'border-danger': formError.includes('letterDate')
                    }"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Author:" label-for="author">
                <vue-select
                    v-model="author"
                    label="fullname"
                    placeholder="Select author"
                    :disabled="isLoadingList"
                    :options="authorOptions"
                    class="show-actions"
                    :clearable="false"
                />
            </b-form-group>

            <b-form-group label-cols="3" label="Patient:" label-for="patient">
                <vue-select
                    v-model="patient"
                    label="name"
                    :placeholder="patientPlaceholder"
                    :disabled="isLoadingList"
                    :options="patientOptions"
                    :clearable="!(template && template.patient_required)"
                    :class="{
                        'show-actions': true,
                        'border-danger': formError.includes('patient')
                    }"
                />
            </b-form-group>

            <b-form-group
                label-cols="3"
                label="Reference:"
                label-for="reference"
            >
                <vue-select
                    v-model="reference"
                    label="name"
                    placeholder="Select medical note"
                    :disabled="isLoadingReference"
                    :options="medicalNoteOptions"
                    class="show-actions"
                    :clearable="false"
                />
            </b-form-group>
        </form>

        <template v-slot:modal-footer>
            <loader v-if="isSaving" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isSaving"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isSaving"
                variant="primary"
                @click="save"
                :disabled="!letterDate || letterDate === ''"
            >
                Save
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import vueSelect from "vue-select";
import ClientDocument from "@/services/client-documents";
import Loader from "@/module/commons/loader";

const referenceDefaultOption = {
    name: "No Medical Note",
    value: "0"
};

export default {
    components: {
        vueSelect,
        Loader
    },
    props: {
        redirect: {
            type: Boolean, // redirect to letter page after save
            default: false
        }
    },
    computed: {
        templatePlaceholder() {
            return this.isLoadingList
                ? "Loading..."
                : "Select template (optional)";
        },
        patientPlaceholder() {
            return this.isLoadingList
                ? "Loading..."
                : this.template && this.template.patient_required
                ? "Select Patient (required)"
                : "Select Patient (optional)";
        }
    },
    data() {
        return {
            template: null,
            title: "",
            patient: null,
            reference: null,
            letterDate: null,
            author: null,
            isSaving: false,
            isOpen: false,
            templateOptions: [],
            patientOptions: [],
            referenceOptions: [],
            medicalNoteOptions: [referenceDefaultOption],
            authorOptions: [],
            isLoadingList: true,
            isLoadingReference: true,
            datePickerOptions: {},
            clientId: null,
            formError: []
        };
    },
    watch: {
        patient(value) {
            this.reference = referenceDefaultOption; // each time patient changed, reset reference value

            if (value) {
                this.fetchReference(value.id);

                this.formError = this.formError.filter(
                    err => err !== "patient"
                );
            }
        },
        letterDate(value) {
            if (value) {
                this.formError = this.formError.filter(
                    err => err !== "letterDate"
                );
            } else {
                this.formError.push("letterDate");
            }
        },
        template(value) {
            this.formError = [];

            if (value) {
                this.title = value.name || "";
            } else {
                this.title = "";
            }
        }
    },
    methods: {
        open() {
            this.isOpen = true;
            this.clientId = this.$store.state.client.client_id; // get client id from vuex store
            this.fetchDropdownList();
            this.letterDate = new Date();

            this.datePickerOptions = {
                format: this.getDateFormat()
            };
        },
        close() {
            this.isOpen = false;
        },
        async fetchDropdownList() {
            this.isLoadingList = true;

            const actorId = this.$root.currentActor.id;
            const getLetterTemplate = ClientDocument.getLetterTemplate();
            const getAuthor = ClientDocument.getAuthor();
            const getPatients = ClientDocument.getPatient(this.clientId);

            const promiseResults = await Promise.all([
                getLetterTemplate,
                getAuthor,
                getPatients,
            ]);

            const [templates, authors, patients] = promiseResults;

            this.isLoadingList = false;
            this.templateOptions = templates;
            this.authorOptions = authors;
            this.patientOptions = patients;

            this.applyDefaultAuthor(actorId, authors);
            this.patient = patients[0] || null;
        },
        async fetchReference(patientId) {
            this.isLoadingReference = true;

            const rawMedicalNotes = await ClientDocument.getPatientMedicalNote(
                patientId
            );

            let notes = [];

            for (const record of rawMedicalNotes) {
                let date = this.displayProperDate(record.create_at);
                let name = `${record.template.label} (${date})`;

                notes.push({
                    value: record.id,
                    name: name
                });
            }

            this.medicalNoteOptions = [referenceDefaultOption, ...notes];
            this.isLoadingReference = false;
        },
        displayProperDate(rawDate) {
            return this._toDateTime(rawDate)
                .setZone(this._timeZone)
                .toFormat(this._getLuxonFormat());
        },
        applyDefaultAuthor(actorId, authors = []) {
            // NOTE: the current actorId will be the default author if it exist

            let currentAuthor = authors && authors.find(
                rec => rec.id === actorId
            );

            if (!currentAuthor) {
                currentAuthor = authors[0];
            }

            this.author = currentAuthor;
        },
        async save() {
            try {
                let hasError = false;
                this.formError = [];

                if (
                    this.template &&
                    this.template.patient_required &&
                    !this.patient
                ) {
                    this.formError.push("patient");
                    hasError = true;
                }

                if (hasError) {
                    return;
                }

                let sid =
                    this.reference && this.reference.value
                        ? this.reference.value
                        : null;

                if (!this.letterDate || this.letterDate === "") {
                    this._message("Date is required", "danger");
                    this.isSaving = false;
                    return;
                }

                const data = {
                    pid: this.patient && this.patient.id ? this.patient.id : "",
                    cid: this.clientId,
                    sid: sid === "0" ? "" : sid,
                    template: this.template ? this.template.id : "",
                    recipient_id: this.clientId,
                    title: this.title,
                    date: this.letterDate,
                    vet_id: this.author.id
                };

                // change date to user's format
                // issue: default time picker value is in date object (if no change event)
                // change to string if necessary
                if (typeof data.date === "object") {
                    data.date = this.displayProperDate(data.date);
                }

                this.isSaving = true;

                const res = await ClientDocument.saveNewLetter(data);

                if (res.message.code == 200) {
                    let result = res.resources;
                    if (result.redirect && result.id) {
                        this.close();
                        this._message("Letter created");
                        this.$router.push({
                            name: "client_letter",
                            params: {
                                id: result.id
                            }
                        });
                    } else {
                        this.close();
                        this._message("Letter created");
                        this.$emit("onSuccess", result);
                    }
                } else {
                    throw "Error: Unable to save";
                }
            } catch (e) {
                this._message("Error: Unable to save", "danger");
                this.isSaving = false;
            }
        }
    }
};
</script>
